// src/features/TunnelAnalysis/TunnelAnalysisPage.js
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  TextField,
  Tabs,
  Tab
} from '@mui/material';
import { 
  Visibility, 
  Analytics,
  TrendingUp,
  Search,
  ShowChart,
  BarChart
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n/i18n';
import { useTunnelData, TUNNEL_CONFIG } from './hooks/useTunnelData';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  BarChart as RechartsBarChart, 
  Bar, 
  Legend 
} from 'recharts';

// --- Компонент графіка для програми тунелю ---
const TunnelChart = ({ data, type = 'line', height = 200 }) => {
  const theme = useTheme();
  const { t } = useI18n();
  
  if (!data || data.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('tunnel_analysis_no_data')}
        </Typography>
      </Box>
    );
  }

  const chartData = data.map(item => ({
    date: new Date(item.date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
    value: item.value,
    fullDate: item.date
  }));

  return (
    <ResponsiveContainer width="100%" height={height}>
      {type === 'line' ? (
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={50}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip 
            formatter={(value) => [`${value} ${t('labels_kg')}`, t('tunnel_analysis_weight')]}
            labelFormatter={(label, payload) => {
              if (payload && payload[0]) {
                return new Date(payload[0].payload.fullDate).toLocaleDateString('es-ES');
              }
              return label;
            }}
          />
          <Line 
            type="monotone" 
            dataKey="value" 
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, fill: theme.palette.primary.dark }}
          />
        </LineChart>
      ) : (
        <RechartsBarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={50}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip 
            formatter={(value) => [`${value} ${t('labels_kg')}`, t('tunnel_analysis_weight')]}
          />
          <Bar 
            dataKey="value" 
            fill={theme.palette.primary.main}
            radius={[4, 4, 0, 0]}
          />
        </RechartsBarChart>
      )}
    </ResponsiveContainer>
  );
};

// Картка програми тунелю з графіком
const TunnelProgramCard = ({ programKey, data, statistics, config, onViewDetails }) => {
  const { t } = useI18n();
  const theme = useTheme();
  const [chartType, setChartType] = useState('line');

  // Останні 10 записів для графіка
  const chartData = data.slice(-10);

  return (
    <Grid item xs={12} sm={6} lg={4}>
      <Card 
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.7)} 100%)`,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 12px 40px 0 rgba(0,0,0,0.12)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          }
        }}
      >
        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Заголовок програми */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                }}
              >
                <TrendingUp sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    lineHeight: 1.2,
                    mb: 0.5
                  }}
                >
                  {config.programName}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip 
                    label={config.unit} 
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip 
                    label={config.type === 'production' ? t('tunnel_type_production') : t('tunnel_type_rejection')}
                    size="small"
                    variant="outlined"
                    color={config.type === 'production' ? 'success' : 'error'}
                    sx={{ fontWeight: 500 }}
                  />
                </Stack>
              </Box>
            </Box>
          </Box>

          {/* Графік */}
          {chartData.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t('tunnel_analysis_trend')}
                </Typography>
                <Select
                  size="small"
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value)}
                  sx={{ minWidth: 100 }}
                >
                  <MenuItem value="line">{t('chart_type_line')}</MenuItem>
                  <MenuItem value="bar">{t('chart_type_bar')}</MenuItem>
                </Select>
              </Box>
              <TunnelChart data={chartData} type={chartType} height={120} />
            </Box>
          )}

          {/* Статистика */}
          {data.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('tunnel_stats_total')}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main' }}>
                    {statistics.total.toLocaleString('es-ES')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('tunnel_stats_avg')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {statistics.average.toFixed(1)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Кнопка деталей */}
          <Box sx={{ mt: 'auto' }}>
            <Button 
              fullWidth
              variant="outlined"
              startIcon={<Visibility />}
              onClick={() => onViewDetails(programKey)}
              sx={{
                borderRadius: '8px',
                py: 1,
                fontWeight: 600,
                borderWidth: '2px',
                '&:hover': {
                  borderWidth: '2px',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              {t('tunnel_analysis_view_details')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default function TunnelAnalysisPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [programType, setProgramType] = useState('all');

  const { allProgramsData, loading, error } = useTunnelData();

  // Фільтруємо програми за пошуком та типом
  const filteredPrograms = useMemo(() => {
    const programs = Object.keys(TUNNEL_CONFIG);
    
    let filtered = programs;
    
    // Фільтр по типу
    if (programType !== 'all') {
      filtered = filtered.filter(programKey => 
        TUNNEL_CONFIG[programKey].type === programType
      );
    }
    
    // Фільтр по пошуку
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(programKey => {
        const config = TUNNEL_CONFIG[programKey];
        return config.programName.toLowerCase().includes(lowerSearchTerm);
      });
    }
    
    return filtered;
  }, [searchTerm, programType]);

  const handleViewDetails = (programKey) => {
    navigate(`/tunnel-analysis/${programKey}`);
  };

  // Програми з даними
  const programsWithData = useMemo(() => {
    return Object.keys(allProgramsData).filter(key => allProgramsData[key]?.data?.length > 0);
  }, [allProgramsData]);

  // Статистика по всіх програмах
  const totalStats = useMemo(() => {
    const productionPrograms = Object.keys(TUNNEL_CONFIG).filter(
      key => TUNNEL_CONFIG[key].type === 'production'
    ).length;
    
    const rejectionPrograms = Object.keys(TUNNEL_CONFIG).filter(
      key => TUNNEL_CONFIG[key].type === 'rejection'
    ).length;

    return {
      totalPrograms: Object.keys(TUNNEL_CONFIG).length,
      productionPrograms,
      rejectionPrograms,
      programsWithData: programsWithData.length
    };
  }, [programsWithData]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          {t('common_loading')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
      {/* Заголовок сторінки */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 60,
              height: 60,
              borderRadius: '16px',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
            }}
          >
            <Analytics sx={{ color: 'white', fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
              {t('tunnel_analysis_title')}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
              {t('tunnel_analysis_subtitle')}
            </Typography>
          </Box>
        </Stack>

        {/* Статистика */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: alpha(theme.palette.primary.main, 0.05) }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main' }}>
                  {totalStats.totalPrograms}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('tunnel_analysis_programs')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: alpha(theme.palette.success.main, 0.05) }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'success.main' }}>
                  {totalStats.productionPrograms}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('tunnel_type_production')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: alpha(theme.palette.error.main, 0.05) }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'error.main' }}>
                  {totalStats.rejectionPrograms}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('tunnel_type_rejection')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: alpha(theme.palette.info.main, 0.05) }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'info.main' }}>
                  {totalStats.programsWithData}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('tunnel_analysis_with_data')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Пошук та фільтри */}
        <Card sx={{ mb: 4, p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label={t('tunnel_analysis_search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />
                }}
                placeholder={t('tunnel_analysis_search_placeholder')}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('tunnel_analysis_filter_type')}</InputLabel>
                <Select
                  value={programType}
                  onChange={(e) => setProgramType(e.target.value)}
                  label={t('tunnel_analysis_filter_type')}
                >
                  <MenuItem value="all">{t('tunnel_type_all')}</MenuItem>
                  <MenuItem value="production">{t('tunnel_type_production')}</MenuItem>
                  <MenuItem value="rejection">{t('tunnel_type_rejection')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                {t('tunnel_analysis_found')}: <strong>{filteredPrograms.length}</strong> {t('tunnel_analysis_programs')}
              </Typography>
            </Grid>
          </Grid>
        </Card>
      </Box>

      {/* Список програм */}
      <Grid container spacing={3}>
        {filteredPrograms.map(programKey => (
          <TunnelProgramCard
            key={programKey}
            programKey={programKey}
            data={allProgramsData[programKey]?.data || []}
            statistics={allProgramsData[programKey]?.statistics || {
              total: 0, average: 0, max: 0, min: 0, daysWithData: 0, recordsCount: 0
            }}
            config={TUNNEL_CONFIG[programKey]}
            onViewDetails={handleViewDetails}
          />
        ))}
      </Grid>

      {/* Інформаційний блок */}
      {totalStats.programsWithData > 0 && (
        <Card 
          sx={{ 
            mt: 4, 
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body1" color="text.secondary">
              {t('tunnel_analysis_click_for_details')}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}


